import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { isNative, isIOS } from './is-native';

/**
 * JS bridge for the native `MuckaLiveActivityPlugin` (see
 * `ios/App/App/MuckaLiveActivityPlugin.swift`). Controls the On Time
 * Tracker iOS Live Activity — the lock-screen + Dynamic Island surface
 * that mirrors the running-late action card.
 *
 * Phase 5a (current): the JS side starts/updates/ends activities while
 * the app is foreground. Phase 5b adds push-to-start so the OS can
 * fire it without the app being open.
 *
 * The `ContentState` shape mirrors the Swift struct in
 * `OnTimeLiveActivity.swift` — keep them in lockstep.
 */

export type Tier = 'on_schedule' | 'leave_soon' | 'leave_now' | 'running_late';

export interface LiveActivityState {
  tier: Tier;
  /** Headline: "On schedule" / "Leave in 8 min" / "Time to go" / "Running 15 min late". */
  title: string;
  /** Eyebrow uppercase tag: "ON SCHEDULE" / "LEAVE SOON" / "TIME TO GO" / "RUNNING LATE". */
  eyebrow: string;
  /** Client first/full name. Null falls back to "your next job". */
  clientName: string | null;
  /** "Boiler service" — the job's type column. */
  jobType: string;
  /** "10:00am" — pre-formatted for display. */
  scheduledLabel: string;
  /** Address line; shown below the title in the lock-screen view. */
  address?: string | null;
  /** "4.8 mi" — pre-formatted client-side. Omit when we don't have a recent fix. */
  distanceLabel?: string | null;
  /** Travel-minutes ETA from current location to the job, when known. */
  etaMinutes?: number | null;
  /**
   * Unix epoch seconds for the moment the engineer should be leaving by.
   * Drives the on-device SwiftUI `Text(_:style:.timer)` countdown so the
   * digits tick without any server push. For `running_late`, this is in
   * the past — SwiftUI handles both directions automatically.
   */
  leaveBy: number;
  /** Pre-drafted SMS — running_late only. */
  draftText?: string | null;
  /** `sms:NUMBER?&body=URLENCODED` — opens iOS Messages when tapped. */
  smsLink?: string | null;
  /** Integer minutes late on arrival; null for non-late tiers. */
  lateMinutes?: number | null;
}

interface PushToStartTokenEvent {
  attributesType: string;
  token: string;
}

interface MuckaLiveActivityPlugin {
  isSupported(): Promise<{ supported: boolean; enabled: boolean }>;
  start(opts: { jobId: string; state: LiveActivityState }): Promise<{ activityId: string; updated: boolean }>;
  update(opts: { jobId: string; state: LiveActivityState }): Promise<{ activityId: string }>;
  end(opts?: { jobId?: string; dismissalPolicy?: 'immediate' | 'after' }): Promise<{ ended: number }>;
  startPushToStartObserver(): Promise<{ started: boolean; reason?: string; duplicate?: boolean }>;
  addListener(
    eventName: 'pushToStartToken',
    listenerFunc: (event: PushToStartTokenEvent) => void,
  ): Promise<PluginListenerHandle>;
}

const MuckaLiveActivity = registerPlugin<MuckaLiveActivityPlugin>('MuckaLiveActivity');

/** True when we can call into the native plugin AND the user hasn't disabled activities in Settings → Live Activities. */
export async function liveActivityAvailable(): Promise<boolean> {
  if (!isNative() || !isIOS()) return false;
  try {
    const { supported, enabled } = await MuckaLiveActivity.isSupported();
    return supported && enabled;
  } catch {
    return false;
  }
}

/**
 * Start (or update, if one already exists for this job) the Live
 * Activity. No-op on web / Android. Errors are swallowed — Live
 * Activities are nice-to-have, never block the UI on them.
 */
export async function startLiveActivity(jobId: string, state: LiveActivityState): Promise<void> {
  if (!(await liveActivityAvailable())) return;
  try {
    await MuckaLiveActivity.start({ jobId, state });
  } catch (err) {
    console.warn('[live-activity] start failed:', err instanceof Error ? err.message : err);
  }
}

/**
 * Update the running activity's state without starting a new one.
 * Rejects natively if no activity exists for the jobId — we suppress
 * that here so the caller can naively call update without checking.
 */
export async function updateLiveActivity(jobId: string, state: LiveActivityState): Promise<void> {
  if (!(await liveActivityAvailable())) return;
  try {
    await MuckaLiveActivity.update({ jobId, state });
  } catch (err) {
    // Most common cause: activity hasn't been started yet. Caller can
    // retry via startLiveActivity if they care.
    console.warn('[live-activity] update failed:', err instanceof Error ? err.message : err);
  }
}

/**
 * End the activity. Passing no jobId ends every activity owned by this
 * skill — useful when the engineer disables the tracker entirely.
 */
export async function endLiveActivity(jobId?: string): Promise<void> {
  if (!isNative() || !isIOS()) return;
  try {
    await MuckaLiveActivity.end({ jobId, dismissalPolicy: 'immediate' });
  } catch (err) {
    console.warn('[live-activity] end failed:', err instanceof Error ? err.message : err);
  }
}

/**
 * Phase 5b — Begin observing push-to-start tokens and POST them to the
 * backend. Idempotent: safe to call on every app boot. No-op on web /
 * Android / iOS < 17.2. The native side dedupes so spinning up the
 * observer twice is harmless.
 *
 * Token rotation is rare in practice (reinstall, OS upgrade) so the
 * `/api/native/live-activity-token` route is fine to upsert blindly.
 */
let pushTokenListener: PluginListenerHandle | null = null;

export async function setupPushToStartTokens(): Promise<void> {
  if (!isNative() || !isIOS()) return;
  try {
    const supported = await liveActivityAvailable();
    if (!supported) return;

    // Attach the listener BEFORE asking the native side to start
    // observing — otherwise we can race past the first token emission.
    if (!pushTokenListener) {
      pushTokenListener = await MuckaLiveActivity.addListener(
        'pushToStartToken',
        (event) => {
          void forwardToken(event);
        },
      );
    }

    await MuckaLiveActivity.startPushToStartObserver();
  } catch (err) {
    console.warn('[live-activity] push-to-start setup failed:', err instanceof Error ? err.message : err);
  }
}

async function forwardToken(event: PushToStartTokenEvent): Promise<void> {
  try {
    const res = await fetch('/api/native/live-activity-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        attributesType: event.attributesType,
        pushToStartToken: event.token,
      }),
    });
    if (!res.ok) {
      console.warn('[live-activity] token POST failed:', res.status);
    }
  } catch (err) {
    console.warn('[live-activity] token POST error:', err instanceof Error ? err.message : err);
  }
}

export default MuckaLiveActivity;
